'use client'

import { useState, useEffect, type FormEvent } from 'react'
import { signIn } from 'next-auth/react'

export type TwoFAStep = 'credentials' | 'totp' | 'email-otp' | 'recovery'

interface Use2FALoginOptions {
  // Each page supplies its own post-authentication action (dashboard redirect vs extension
  // token handoff). Called once a session has actually been issued.
  onAuthenticated: () => unknown
}

// Shared login + second-factor flow for the web login pages (app/login and app/extension-login).
// Owns email/password/code state and the signIn() calls, and catches the errors lib/auth.ts
// authorize() throws — TOTP_REQUIRED, PASSKEY_REQUIRED, EMAIL_OTP_REQUIRED — routing each to the
// right step. Page-specific UI (branding, the passwordless passkey button, redirect target) stays
// in the page; only the 2FA-step handling lives here so both pages behave identically.
export function use2FALogin({ onAuthenticated }: Use2FALoginOptions) {
  const [step, setStep] = useState<TwoFAStep>('credentials')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('') // TOTP or email-OTP code, depending on `step`
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [passkeyBusy, setPasskeyBusy] = useState(false)
  // Seconds until an email-OTP resend is allowed again. Matches the server's 60s
  // per-user send cooldown so the "Resend code" button reflects reality (before this,
  // a resend within the window silently no-op'd on the server and looked broken).
  const [resendCooldown, setResendCooldown] = useState(0)

  useEffect(() => {
    if (resendCooldown <= 0) return
    const t = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000)
    return () => clearTimeout(t)
  }, [resendCooldown])

  // Run a WebAuthn assertion SCOPED to this email (allowCredentials from the user's passkeys, not
  // the discoverable/passwordless flow) and return a passkeyToken, or null on failure/cancel.
  async function runScopedPasskey(emailForAssertion: string): Promise<string | null> {
    setPasskeyBusy(true)
    try {
      const { startAuthentication } = await import('@simplewebauthn/browser')
      const beginRes = await fetch('/api/auth/passkey/authenticate/begin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailForAssertion }),
      })
      if (!beginRes.ok) return null
      const options = await beginRes.json()
      const { _challengeKey, ...optionsJSON } = options
      const authResponse = await startAuthentication(optionsJSON, false)
      const completeRes = await fetch('/api/auth/passkey/authenticate/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...authResponse, email: emailForAssertion, _challengeKey }),
      })
      const result = await completeRes.json()
      return result?.success ? (result.passkeyToken as string) : null
    } catch {
      return null
    } finally {
      setPasskeyBusy(false)
    }
  }

  async function submitCredentials(e?: FormEvent) {
    e?.preventDefault()
    if (loading || passkeyBusy) return
    setLoading(true)
    setError('')

    const result = await signIn('credentials', { email, password, redirect: false })
    const err = (result?.error || '').toUpperCase()

    if (err.includes('RATE_LIMITED')) {
      // Too many failed attempts for this IP — surface the lockout (with an approximate
      // wait) instead of the generic "invalid email or password".
      setLoading(false)
      const m = err.match(/RATE_LIMITED:(\d+)/)
      const mins = m ? Math.ceil(parseInt(m[1], 10) / 60) : 0
      setError(mins > 0
        ? `Too many attempts. Try again in about ${mins} minute${mins === 1 ? '' : 's'}.`
        : 'Too many attempts. Please wait a few minutes and try again.')
      return
    }

    if (err.includes('TOTP')) {
      setLoading(false)
      setStep('totp')
      return
    }

    if (err.includes('PASSKEY_REQUIRED')) {
      // Passkey is the enrolled second factor: run the WebAuthn ceremony (browser prompts the
      // authenticator), then resubmit with password + passkeyToken so authorize() verifies both.
      // If the passkey isn't available (lost device, cancelled), fall back to a recovery code.
      setLoading(false)
      const token = await runScopedPasskey(email)
      if (!token) {
        setError("Passkey didn't work — you can use a recovery code instead")
        setCode('')
        setStep('recovery')
        return
      }
      setLoading(true)
      const r2 = await signIn('credentials', { email, password, passkeyToken: token, redirect: false })
      setLoading(false)
      if (r2?.ok) { await onAuthenticated(); return }
      setError("Passkey verification failed — you can use a recovery code instead")
      setCode('')
      setStep('recovery')
      return
    }

    if (err.includes('EMAIL_OTP_REQUIRED')) {
      // Email OTP is the enrolled second factor — a code has been dispatched by the enrolled path.
      setLoading(false)
      setStep('email-otp')
      setResendCooldown(60) // a code was just sent; block resend until the server cooldown clears
      return
    }

    setLoading(false)
    if (result?.ok) { await onAuthenticated(); return }
    setError('Invalid email or password')
  }

  async function submitCode(e?: FormEvent) {
    e?.preventDefault()
    if (loading) return
    setLoading(true)
    setError('')

    const field = step === 'email-otp' ? { emailOtpCode: code }
      : step === 'recovery' ? { recoveryCode: code }
      : { totpCode: code }
    const result = await signIn('credentials', { email, password, ...field, redirect: false })
    setLoading(false)

    if (result?.ok) { await onAuthenticated(); return }
    setError(step === 'email-otp'
      ? 'Invalid or expired code — request a new one and try again'
      : step === 'recovery'
      ? 'Invalid recovery code — each code works only once'
      : 'Invalid code — check your authenticator app and try again')
    setCode('')
  }

  // Switch to recovery-code entry (offered during a TOTP/passkey challenge).
  function useRecoveryCode() {
    setError('')
    setCode('')
    setStep('recovery')
  }

  // Fallback: email the user a one-time code and switch to the code-entry step. Available during
  // the TOTP and passkey prompts too, so a user without their authenticator/passkey can still sign
  // in. Wired to the /api/auth/email-otp/send endpoint (see Part 2).
  async function requestEmailFallback() {
    if (loading || passkeyBusy || resendCooldown > 0) return
    setError('')
    // Start the cooldown immediately so rapid double-clicks can't fire two sends.
    setResendCooldown(60)
    try {
      const res = await fetch('/api/auth/email-otp/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, purpose: 'fallback' }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setResendCooldown(0) // let them retry immediately on a real failure
        setError(data.error || 'Could not send a code. Please try again.')
        return
      }
      setCode('')
      setStep('email-otp')
    } catch {
      setResendCooldown(0)
      setError('Could not send a code. Please try again.')
    }
  }

  function back() {
    setStep('credentials')
    setError('')
    setCode('')
    setResendCooldown(0)
  }

  // Full reset to an empty login form (email/password cleared too) — for the extension's "Retry".
  function reset() {
    setStep('credentials')
    setEmail('')
    setPassword('')
    setCode('')
    setError('')
    setLoading(false)
    setPasskeyBusy(false)
    setResendCooldown(0)
  }

  return {
    step, setStep,
    email, setEmail,
    password, setPassword,
    code, setCode,
    error, setError,
    loading, passkeyBusy, resendCooldown,
    submitCredentials, submitCode, requestEmailFallback, useRecoveryCode, back, reset,
  }
}
